import { stumble } from '@ipfabric/frontend-shared-assets/utils/stumble/stumble';
import { useEffect, useRef, useState } from 'react';

export type ScrolledContentState = 'no-scroll' | 'top' | 'middle' | 'bottom';

export const useScrolledContentState = () => {
  const scrollableContentRef = useRef<HTMLDivElement>(null);

  const [scrolledContentState, setScrolledContentState] =
    useState<ScrolledContentState>('no-scroll');

  // Check on mount, resize, and scroll
  useEffect(() => {
    const currentRef = scrollableContentRef.current;
    if (!currentRef) {
      return;
    }

    const updateShadowVisibility = (element: Element) => {
      const { scrollTop, scrollHeight, clientHeight } = element;
      const isOverflowing = scrollHeight > clientHeight;
      if (!isOverflowing) {
        setScrolledContentState('no-scroll');
        return;
      }

      if (Math.floor(scrollTop) <= 0) {
        setScrolledContentState('top');
        return;
      }

      if (Math.ceil(scrollTop + clientHeight) >= scrollHeight) {
        setScrolledContentState('bottom');
        return;
      }

      setScrolledContentState('middle');
    };

    // Create resize observer for content, so that we don't react when the size doesn't impact the content box
    const resizeObserver = new ResizeObserver(([scrollableContent]) => {
      if (scrollableContent === undefined) {
        stumble(
          'ResizeObserver callback called with no entries. Skipping shadow visibility update.',
        );
        return;
      }

      updateShadowVisibility(scrollableContent.target);
    });

    // Function to check and update shadow visibility
    const updateShadowVisibilityForCurrentRef = () => {
      if (scrollableContentRef.current) {
        updateShadowVisibility(scrollableContentRef.current);
      }
    };

    // Initial check and resize listener
    updateShadowVisibilityForCurrentRef();
    resizeObserver.observe(currentRef);

    // Add scroll listener
    currentRef.addEventListener('scroll', updateShadowVisibilityForCurrentRef);

    return () => {
      resizeObserver.disconnect();
      currentRef.removeEventListener(
        'scroll',
        updateShadowVisibilityForCurrentRef,
      );
    };
  }, []);

  return {
    scrollableContentRef,
    scrolledContentState,
  };
};
